import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SkeletonBlockComponent } from '../skeleton/skeleton-block/skeleton-block.component';

@Component({
  selector: 'xemex-detail-field',
  standalone: true,
  imports: [SkeletonBlockComponent, MatTooltipModule],
  styleUrl: './detail-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './detail-field.component.html',
})
export class DetailFieldComponent {
  label = input.required<string>();
  loading = input<boolean>(false);
  /** Keep the value on a single line with an ellipsis, revealing the full text on hover. */
  truncate = input<boolean>(false);
  valueContent = viewChild<ElementRef<HTMLElement>>('valueContent');
  showFallback = signal(false);
  /** True when truncation is on and the value is actually clipped — gates the tooltip. */
  isTruncated = signal(false);
  /** Full text of the value, used as the hover tooltip when truncated. */
  tooltipText = signal('');

  constructor() {
    effect(onCleanup => {
      if (this.loading()) {
        this.showFallback.set(false);
        this.isTruncated.set(false);
        return;
      }

      const valueContent = this.valueContent()?.nativeElement;
      if (!valueContent) {
        return;
      }

      const truncate = this.truncate();
      const sync = () => {
        const text = valueContent.textContent?.trim() ?? '';
        this.showFallback.set(!text.length);
        if (truncate) {
          this.tooltipText.set(text);
          this.isTruncated.set(valueContent.scrollWidth > valueContent.clientWidth);
        }
      };

      sync();

      const observer = new MutationObserver(sync);
      observer.observe(valueContent, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      onCleanup(() => observer.disconnect());
    });
  }
}
